import { isUndefined } from 'lodash';
import { AccountDto } from 'src/dtos';
import {
  Account,
  AccountFinancialAssets,
  AccountHolderAddress,
  AccountSuitabilityInformation,
  AccountType,
  EntityAccountType,
  JointAccountType,
  RetirementAccountType,
} from 'src/models';
import { getUnitedStateOptionLabel } from 'src/utils';

import { mapAccountHolderDtoToModel } from './accountHolders.mappers';

const getCustomTypeLabel = ({
  type,
  retirementAccountType,
  entityAccountType,
}: {
  type: AccountType;
  retirementAccountType?: RetirementAccountType;
  entityAccountType?: EntityAccountType;
}) => {
  if (type.isRetirement) {
    return retirementAccountType?.label ?? type.label;
  }

  if (type.isEntity) {
    return entityAccountType?.label ?? type.label;
  }

  return type.label;
};

export const mapAccountDtoToModel = (dto: AccountDto): Account => {
  const type = new AccountType(dto.type);
  const jointAccountType = type.isJoint ? new JointAccountType(dto.jointAccountType) : undefined;
  const retirementAccountType = type.isRetirement ? new RetirementAccountType(dto.retirementAccountType) : undefined;
  const entityAccountType = type.isEntity ? new EntityAccountType(dto.entityAccountType) : undefined;

  let secondaryAccountHolder = dto.secondaryAccountHolder;

  if (type.isEntity && dto.otherAccountHolders && dto.otherAccountHolders.length > 0) {
    secondaryAccountHolder = dto.otherAccountHolders[0];
  }

  return {
    id: dto.id,
    type,
    customTypeLabel: getCustomTypeLabel({
      type,
      retirementAccountType,
      entityAccountType,
    }),
    jointAccountType,
    jointCommunityPropertyState: dto.jointCommunityPropertyState
      ? { label: getUnitedStateOptionLabel(dto.jointCommunityPropertyState), value: dto.jointCommunityPropertyState }
      : undefined,
    accountId: dto.accountId,
    accountNumber: dto.accountNumber,
    isRoot: dto.isRoot,
    isAccredited: dto.isAccredited,
    primaryAccountHolder: dto.primaryAccountHolder && mapAccountHolderDtoToModel(dto.primaryAccountHolder),
    secondaryAccountHolder: secondaryAccountHolder && mapAccountHolderDtoToModel(secondaryAccountHolder), // TODO: pass authroized signer 2 as secondary account holder if found in enttiy account
    retirementAccountType,
    simpleIraPlanName: dto.simpleIraPlanName,
    beneficiaryIraDecedentName: dto.beneficiaryIraDecedentName,
    beneficiaries: dto.beneficiaries ?? [],
    entityAccountType,
    isEntityAccountTypeActive: type.isEntity ? !isUndefined(dto.accountNumber) : undefined,
    otherAccountHolders:
      dto.otherAccountHolders?.filter((holder, index) => index > 0).map(mapAccountHolderDtoToModel) ?? [],
    entityInformation: dto.entityInformation
      ? { ...dto.entityInformation, address: new AccountHolderAddress(dto.entityInformation.address) }
      : undefined,
    applicationId: dto.applicationId,
    suitabilityInformation: dto.suitabilityInformation
      ? new AccountSuitabilityInformation(dto.suitabilityInformation)
      : undefined,
    financialAssets: dto.financialAssets ? new AccountFinancialAssets(dto.financialAssets) : undefined,
    failedCreateAttempts: dto.failedCreateAttempts,
    lastFailedCreateAttemptError: dto.lastFailedCreateAttemptError,
    lastFailedCreateAttemptAt: dto.lastFailedCreateAttemptAt,
    failedLinkAttempts: dto.failedLinkAttempts,
    lastFailedLinkAttemptError: dto.lastFailedLinkAttemptError,
    lastFailedLinkAttemptAt: dto.lastFailedLinkAttemptAt,
    createdAt: dto.createdAt,
  };
};
